import { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import { noop } from '@frontend/utils';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { useFeatureFlagValue } from 'store/session/hooks';
import { getAvailableGroups, getIsSuperuser } from 'store/session/selectors';

type DashboardViewContext = {
  isMultisiteDashboardToggled?: boolean;
  canToggleDashboardView: boolean;
  toggleDashboardView: () => void;
};

const DashboardViewContext = createContext<DashboardViewContext>({
  isMultisiteDashboardToggled: false,
  canToggleDashboardView: false,
  toggleDashboardView: noop,
});

export const useDashboardViewContext = () => {
  return useContext(DashboardViewContext);
};

type DashboardViewProviderProps = {
  children?: React.ReactNode;
};

export const DashboardViewProvider = ({
  children,
}: DashboardViewProviderProps) => {
  const isMultisiteFunctionalityEnabled = useFeatureFlagValue(
    featureFlagKeys.isMultisiteFunctionalityAvailable,
  );
  const isSuperUser = useAppSelector(getIsSuperuser);
  const availableGroups = useAppSelector(getAvailableGroups);

  const canToggleDashboardView = useMemo(() => {
    return (
      isMultisiteFunctionalityEnabled &&
      isSuperUser &&
      availableGroups.length > 1
    );
  }, [availableGroups, isMultisiteFunctionalityEnabled, isSuperUser]);

  const [isMultisiteDashboardToggled, setIsMultisiteDashboardToggled] =
    useLocalStorage('isMultisiteDashboardToggled', canToggleDashboardView);

  const toggleDashboardView = useCallback(
    (toggleOverride?: boolean) => {
      const nextValue = toggleOverride ?? !isMultisiteDashboardToggled;
      setIsMultisiteDashboardToggled(nextValue);
    },
    [isMultisiteDashboardToggled, setIsMultisiteDashboardToggled],
  );

  const toggleDashboardViewFunc = useMemo(() => {
    return canToggleDashboardView ? toggleDashboardView : noop;
  }, [canToggleDashboardView, toggleDashboardView]);

  const contextValue = useMemo(() => {
    return {
      isMultisiteDashboardToggled,
      canToggleDashboardView,
      toggleDashboardView: toggleDashboardViewFunc,
    };
  }, [
    canToggleDashboardView,
    isMultisiteDashboardToggled,
    toggleDashboardViewFunc,
  ]);

  return (
    <DashboardViewContext.Provider value={contextValue}>
      {children}
    </DashboardViewContext.Provider>
  );
};
